import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { fmtMoney, fmtDate } from '../../utils/formatters'

export default function StoreRequestsPage() {
  var { user } = useAuth()
  var [requests, setRequests] = useState([])
  var [loading, setLoading] = useState(true)
  var [selected, setSelected] = useState(null)
  var [filter, setFilter] = useState('pending')
  var [declining, setDeclining] = useState(null)
  var [declineReason, setDeclineReason] = useState('')
  var [accepting, setAccepting] = useState(false)

  var fetchRequests = useCallback(async function() {
    setLoading(true)
    var q = supabase.from('store_requests').select('*').order('created_at', { ascending: false })
    if (filter !== 'all') q = q.eq('status', filter)
    var { data } = await q
    setRequests(data || [])
    setLoading(false)
  }, [filter])

  useEffect(function() { fetchRequests() }, [fetchRequests])

  var handleAccept = async function(req) {
    console.log('Accept clicked for request:', req && req.id)
    if (!req || !req.id) { alert('No request selected'); return }
    setAccepting(true)
    try {
      // Build line items and description
      var descParts = []
      var total = 0
      if (req.line_items) { req.line_items.forEach(function(li) { descParts.push(li.description + (li.price === 'TBD' ? ' (TBD)' : ' - ' + fmtMoney(li.price))); if (li.price !== 'TBD') total += (parseFloat(li.price) || 0) }) }
      if (req.extras) { req.extras.forEach(function(ex) { descParts.push(ex.description + ' - ' + fmtMoney(ex.price)); total += (parseFloat(ex.price) || 0) }) }
      if (req.custom_items) { req.custom_items.forEach(function(ci) { descParts.push(ci.description + (ci.price === 'TBD' ? ' (TBD)' : ' - ' + fmtMoney(ci.price))); if (ci.price !== 'TBD') total += (parseFloat(ci.price) || 0) }) }
      if (req.half_sunk) descParts.push('Half Sunk Aboveground (TBD)')
      if (req.tear_down) descParts.push('Pool Tear Down & Place by Road - $500')
      if (req.notes) descParts.push('Notes: ' + req.notes)
      descParts.push('Store: ' + (req.store_name || 'Store Partner'))

      var { data: bid, error: bidErr } = await supabase.from('bids').insert({
        business_id: 'pools',
        bid_type: req.pool_type || 'above_ground',
        client_name: req.client_name || '',
        client_phone: req.client_phone || '',
        client_email: req.client_email || '',
        site_address: req.site_address || '',
        job_name: (req.client_name || '') + ' - ' + (req.pool_type || 'Pool Install').replace(/_/g, ' '),
        total: total,
        description: descParts.join('\n'),
        start_date: req.preferred_date || null,
        status: 'draft',
        created_by: user.id,
      }).select().single()

      if (bidErr || !bid) {
        console.error('Accept: bid insert failed', bidErr)
        alert('Failed to create bid: ' + (bidErr ? bidErr.message : 'Unknown error'))
        return
      }

      var { error: updErr } = await supabase.from('store_requests')
        .update({ status: 'accepted', bid_id: bid.id, updated_at: new Date().toISOString() })
        .eq('id', req.id)
      if (updErr) {
        console.error('Accept: request update failed', updErr)
        alert('Bid created but failed to mark request accepted: ' + updErr.message)
        return
      }

      if (req.store_user_id) {
        // Supabase v2 query builders don't have .catch() — errors come back
        // as { error } from the awaited result. Chaining .catch() directly on
        // .insert() threw "Ee.N.from(...).insert(...).catch is not a function"
        // and aborted the whole handleAccept flow after the bid was created.
        var notifRes = await supabase.from('notifications').insert({
          user_id: req.store_user_id,
          type: 'request_approved',
          title: 'Request Accepted',
          message: (req.client_name || 'Your request') + ' has been accepted and is being processed.',
          link: '/store?request=' + req.id,
          related_id: req.id,
          business_id: 'pools'
        })
        if (notifRes.error) console.warn('Accept: notification failed', notifRes.error)
      }
      alert('Request accepted! Bid created \u2014 you can now review it on the Bids page.')
      await fetchRequests()
      setSelected(null)
    } catch (e) {
      console.error('Accept error:', e)
      alert('Something went wrong: ' + (e && e.message ? e.message : e))
    } finally {
      setAccepting(false)
    }
  }

  var handleDecline = async function(req) {
    console.log('Decline clicked for request:', req && req.id, 'reason:', declineReason)
    if (!req || !req.id) { alert('No request selected'); return }
    var reason = (declineReason || '').trim()
    if (!reason) { alert('Please enter a reason for declining.'); return }
    setDeclining(function(cur) { return cur ? Object.assign({}, cur, { _saving: true }) : cur })
    try {
      var { error: updErr } = await supabase.from('store_requests')
        .update({ status: 'declined', decline_reason: reason, updated_at: new Date().toISOString() })
        .eq('id', req.id)
      if (updErr) {
        console.error('Decline: update failed', updErr)
        alert('Failed to decline request: ' + updErr.message)
        setDeclining(function(cur) { return cur ? Object.assign({}, cur, { _saving: false }) : cur })
        return
      }

      if (req.store_user_id) {
        var declineNotifRes = await supabase.from('notifications').insert({
          user_id: req.store_user_id,
          type: 'request_declined',
          title: 'Request Declined',
          message: (req.client_name || 'Your request') + ' was declined. Reason: ' + reason,
          link: '/store?request=' + req.id,
          related_id: req.id,
          business_id: 'pools'
        })
        if (declineNotifRes.error) console.warn('Decline: notification failed', declineNotifRes.error)
      }

      setDeclining(null)
      setDeclineReason('')
      setSelected(null)
      await fetchRequests()
    } catch (e) {
      console.error('Decline error:', e)
      alert('Something went wrong: ' + (e && e.message ? e.message : e))
      setDeclining(function(cur) { return cur ? Object.assign({}, cur, { _saving: false }) : cur })
    }
  }

  var DECLINE_QUICK_REASONS = [
    'Out of our service area',
    'Too far out',
    'No availability',
    'Customer uncooperative',
  ]

  var pendingCount = requests.filter(function(r) { return r.status === 'pending' }).length
  var stColor = function(st) { return st === 'pending' ? '#b48c3c' : st === 'accepted' ? '#4a8c6a' : st === 'declined' ? '#e05c5c' : 'rgba(255,255,255,0.3)' }

  return (
    <div style={st.container}>
      <div style={st.header}>
        <div>
          <div style={st.title}>STORE REQUESTS</div>
          <div style={st.sub}>{pendingCount} pending {String.fromCodePoint(0x00B7)} {requests.length} total</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
        {['pending', 'accepted', 'declined', 'all'].map(function(f) {
          return <button key={f} onClick={function() { setFilter(f) }} style={{ ...st.filterBtn, ...(filter === f ? st.filterBtnActive : {}) }}>{f.toUpperCase()}{f === 'pending' && pendingCount > 0 ? ' (' + pendingCount + ')' : ''}</button>
        })}
      </div>

      {loading ? <div style={st.empty}>Loading...</div> : requests.length === 0 ? <div style={st.empty}>No {filter === 'all' ? '' : filter + ' '}requests.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {requests.map(function(r) {
            return (
              <div key={r.id} style={{ ...st.card, borderLeft: '3px solid ' + stColor(r.status), cursor: 'pointer' }} onClick={function() { setSelected(selected && selected.id === r.id ? null : r) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#ffffff' }}>{r.client_name}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{r.site_address}{r.preferred_date ? ' | ' + fmtDate(r.preferred_date) : ''}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '2px' }}>{r.store_name} | {r.pool_type ? r.pool_type.replace(/_/g, ' ') : ''} {r.pool_size || ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', color: stColor(r.status) }}>{(r.status || '').toUpperCase()}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '2px' }}>{fmtDate(r.created_at)}</div>
                  </div>
                </div>

                {selected && selected.id === r.id && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: '16px' }}>
                      {r.client_phone && <div><div style={st.detLabel}>PHONE</div><div style={st.detVal}>{r.client_phone}</div></div>}
                      {r.client_email && <div><div style={st.detLabel}>EMAIL</div><div style={st.detVal}>{r.client_email}</div></div>}
                      {r.pool_type && <div><div style={st.detLabel}>POOL TYPE</div><div style={st.detVal}>{r.pool_type.replace(/_/g, ' ').toUpperCase()}</div></div>}
                      {r.pool_size && <div><div style={st.detLabel}>SIZE</div><div style={st.detVal}>{r.pool_size}</div></div>}
                      {r.preferred_date && <div><div style={st.detLabel}>PREFERRED DATE</div><div style={st.detVal}>{fmtDate(r.preferred_date)}</div></div>}
                      {r.store_name && <div><div style={st.detLabel}>STORE</div><div style={st.detVal}>{r.store_name}</div></div>}
                    </div>
                    {r.half_sunk && <div style={{ fontSize: '12px', color: '#b48c3c', marginBottom: '6px' }}>Half Sunk Aboveground — pricing TBD (call to verify)</div>}
                    {r.tear_down && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>Pool Tear Down & Place by Road — $500</div>}
                    {r.line_items && r.line_items.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={st.detLabel}>LINE ITEMS</div>
                        {r.line_items.map(function(li, i) { return <div key={i} style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', padding: '4px 0' }}>{li.description} — <span style={{ color: li.price === 'TBD' ? '#b48c3c' : '#ffffff', fontWeight: 600 }}>{li.price === 'TBD' ? 'TBD' : fmtMoney(li.price)}</span></div> })}
                      </div>
                    )}
                    {r.extras && r.extras.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={st.detLabel}>EXTRAS</div>
                        {r.extras.map(function(ex, i) { return <div key={i} style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', padding: '4px 0' }}>{ex.description} — <span style={{ color: '#ffffff', fontWeight: 600 }}>{fmtMoney(ex.price)}</span></div> })}
                      </div>
                    )}
                    {r.custom_items && r.custom_items.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={st.detLabel}>CUSTOM ITEMS</div>
                        {r.custom_items.map(function(ci, i) { return <div key={i} style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', padding: '4px 0' }}>{ci.description} — <span style={{ color: ci.price === 'TBD' ? '#b48c3c' : '#ffffff', fontWeight: 600 }}>{ci.price === 'TBD' ? 'TBD' : fmtMoney(ci.price)}</span></div> })}
                      </div>
                    )}
                    {r.notes && <div style={{ marginBottom: '12px' }}><div style={st.detLabel}>NOTES</div><div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', whiteSpace: 'pre-wrap' }}>{r.notes}</div></div>}

                    {r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button type="button" disabled={accepting} onClick={function(e) { e.stopPropagation(); e.preventDefault(); handleAccept(r) }} style={{ flex: 1, padding: '12px', backgroundColor: accepting ? 'rgba(74,140,106,0.5)' : '#4a8c6a', border: 'none', borderRadius: '3px', color: '#ffffff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: accepting ? 'wait' : 'pointer' }}>{accepting ? 'CREATING BID...' : 'ACCEPT & CREATE BID'}</button>
                        <button type="button" onClick={function(e) { e.stopPropagation(); e.preventDefault(); console.log('Decline button tapped for', r.id); setDeclineReason(''); setDeclining(r) }} style={{ padding: '12px 20px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' }}>DECLINE</button>
                      </div>
                    )}
                    {r.status === 'accepted' && r.bid_id && <div style={{ fontSize: '12px', color: '#4a8c6a', marginTop: '8px' }}>Bid created — view on the Bids page</div>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {declining && <div style={st.overlay} onClick={function() { if (!declining._saving) { setDeclining(null); setDeclineReason('') } }}>
        <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '2px', color: '#e05c5c', marginBottom: '8px' }}>DECLINE REQUEST</div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px' }}>Decline this request from <span style={{ color: '#ffffff', fontWeight: 600 }}>{declining.client_name}</span>{declining.store_name ? ' (' + declining.store_name + ')' : ''}?</div>
          <div style={{ marginBottom: '10px' }}>
            <div style={st.detLabel}>QUICK REASONS</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
              {DECLINE_QUICK_REASONS.map(function(qr) {
                var active = declineReason === qr
                return <button key={qr} type="button" onClick={function() { setDeclineReason(qr) }} style={{ padding: '6px 12px', backgroundColor: active ? 'rgba(224,92,92,0.2)' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (active ? 'rgba(224,92,92,0.5)' : 'rgba(255,255,255,0.1)'), borderRadius: '16px', color: active ? '#e05c5c' : 'rgba(255,255,255,0.6)', fontSize: '12px', cursor: 'pointer' }}>{qr}</button>
              })}
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={st.detLabel}>REASON (REQUIRED)</div>
            <textarea value={declineReason} onChange={function(e) { setDeclineReason(e.target.value) }} autoFocus style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box', minHeight: '80px', resize: 'vertical' }} placeholder="Tell the store partner why this was declined..." />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" disabled={!!declining._saving} onClick={function() { setDeclining(null); setDeclineReason('') }} style={{ padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: declining._saving ? 'not-allowed' : 'pointer' }}>CANCEL</button>
            <button type="button" disabled={!!declining._saving || !declineReason.trim()} onClick={function() { handleDecline(declining) }} style={{ flex: 1, padding: '10px', backgroundColor: declining._saving || !declineReason.trim() ? 'rgba(224,92,92,0.4)' : '#e05c5c', border: 'none', borderRadius: '3px', color: '#ffffff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: declining._saving || !declineReason.trim() ? 'not-allowed' : 'pointer' }}>{declining._saving ? 'DECLINING...' : 'CONFIRM DECLINE'}</button>
          </div>
        </div>
      </div>}
    </div>
  )
}

var st = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '900px' },
  header: { marginBottom: '24px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  sub: { fontSize: '14px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  filterBtn: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  filterBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  card: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '16px 20px' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '14px' },
  detLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.25)', marginBottom: '3px' },
  detVal: { fontSize: '13px', color: 'rgba(255,255,255,0.7)' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '28px', width: '100%', maxWidth: '450px' },
}
